import { useEffect, useState } from 'react';
import type { PropertyCredentials, TourDataSource } from './sources';

/**
 * Drives credential fetch, source initialization, date refresh on tour-type
 * change, and time-slot fetch on date selection. Loading flags follow the
 * adapter's declared semantics (slotsCachedWithDates, refetchesOnTourTypeChange)
 * so the UI never branches on source id.
 */
export function useTourSchedulerData({
  source,
  javascriptId,
  selectedTourType,
  selectedDate,
}: {
  source: TourDataSource;
  javascriptId: string | null | undefined;
  selectedTourType: string | null;
  selectedDate: string | null;
}): {
  credentials: PropertyCredentials | null;
  tourTypes: string[];
  availableDates: string[];
  timeSlots: string[];
  isInitializing: boolean;
  availabilityLoading: boolean;
  timeSlotsLoading: boolean;
} {
  const [credentials, setCredentials] = useState<PropertyCredentials | null>(null);
  const [tourTypes, setTourTypes] = useState<string[]>([]);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [timeSlots, setTimeSlots] = useState<string[]>([]);
  const [isInitializing, setIsInitializing] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [timeSlotsLoading, setTimeSlotsLoading] = useState(false);

  useEffect(() => {
    setCredentials(null);
    setTourTypes([]);
    setAvailableDates([]);
    setTimeSlots([]);

    if (!javascriptId) {
      setIsInitializing(false);
      return;
    }

    let cancelled = false;
    setIsInitializing(true);

    (async () => {
      const creds = await source.fetchCredentials(javascriptId);
      if (cancelled) return;
      if (!creds) {
        setIsInitializing(false);
        return;
      }
      setCredentials(creds);
      const init = await source.initialize(creds);
      if (cancelled) return;
      setTourTypes(init.tourTypes);
      if (init.dates) setAvailableDates(init.dates);
      setIsInitializing(false);
    })();

    return () => { cancelled = true; };
  }, [source, javascriptId]);

  useEffect(() => {
    if (!credentials || !selectedTourType) return;
    if (!source.refetchesOnTourTypeChange) return;

    let cancelled = false;
    setAvailableDates([]);
    setTimeSlots([]);
    setAvailabilityLoading(true);

    source.fetchAvailableDates(credentials, { tourType: selectedTourType }).then((dates) => {
      if (cancelled) return;
      setAvailableDates(dates);
      setAvailabilityLoading(false);
    });

    return () => { cancelled = true; };
  }, [source, credentials, selectedTourType]);

  useEffect(() => {
    if (!credentials || !selectedDate) {
      setTimeSlots([]);
      return;
    }

    let cancelled = false;
    const showLoading = !source.slotsCachedWithDates;
    if (showLoading) setTimeSlotsLoading(true);

    source.fetchTimeSlotsForDate(credentials, selectedDate, selectedTourType).then((slots) => {
      if (cancelled) return;
      setTimeSlots(slots);
      if (showLoading) setTimeSlotsLoading(false);
    });

    return () => { cancelled = true; };
  }, [source, credentials, selectedDate, selectedTourType]);

  return {
    credentials,
    tourTypes,
    availableDates,
    timeSlots,
    isInitializing,
    availabilityLoading,
    timeSlotsLoading,
  };
}
